const LOCALHOST_HOSTS = new Set(['localhost', '127.0.0.1']);
const DEFAULT_ORIGINAL_PORT = '5173';
const DEFAULT_BM_PORT = '5175';
const KNOWN_CLIENT_TYPE_HOST_LABELS = new Set(['bm', 'ht']);

const resolveEnvPort = (key: 'VITE_ORIGINAL_PORT' | 'VITE_BM_PORT', fallback: string): string => {
  const env = (import.meta as { env?: Record<string, string | boolean | undefined> }).env;
  const rawValue = env?.[key];
  if (typeof rawValue === 'string') {
    const trimmed = rawValue.trim();
    if (trimmed) {
      return trimmed;
    }
  }
  return fallback;
};

const resolveOriginalPort = (): string => resolveEnvPort('VITE_ORIGINAL_PORT', DEFAULT_ORIGINAL_PORT);
const resolveBmPort = (): string => resolveEnvPort('VITE_BM_PORT', DEFAULT_BM_PORT);

const isProductionEnv = (): boolean => {
  const env = (import.meta as { env?: Record<string, string | boolean | undefined> }).env;
  if (typeof env?.PROD === 'boolean') {
    return env.PROD;
  }
  if (typeof env?.PROD === 'string') {
    return env.PROD === 'true';
  }
  if (typeof env?.MODE === 'string') {
    return env.MODE === 'production';
  }
  if (typeof env?.NODE_ENV === 'string') {
    return env.NODE_ENV === 'production';
  }
  return false;
};

export const normalizeClientType = (value?: string | null): string => {
  const normalized = (value ?? '').toString().trim().toLowerCase();
  if (!normalized || normalized === 'original') {
    return '';
  }
  return normalized;
};

export const clientTypeOptions = [
  { label: '標準', value: '' },
  { label: 'ビルメンテ', value: 'bm' },
];

export const formatClientTypeLabel = (value?: string | null): string => {
  const normalized = normalizeClientType(value);
  return normalized === 'bm' ? 'ビルメンテ' : '標準';
};

const splitHostLabels = (hostname: string): { subdomains: string[]; baseDomain: string } => {
  const parts = hostname
    .split('.')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length <= 2) {
    return {
      subdomains: [],
      baseDomain: hostname,
    };
  }

  return {
    subdomains: parts.slice(0, -2).map((part) => normalizeClientType(part)).filter(Boolean),
    baseDomain: parts.slice(-2).join('.'),
  };
};

export const resolveClientTypeFromHost = (hostname: string): string => {
  if (!hostname || LOCALHOST_HOSTS.has(hostname)) {
    return '';
  }

  const { subdomains } = splitHostLabels(hostname);
  return subdomains.find((part) => KNOWN_CLIENT_TYPE_HOST_LABELS.has(part)) ?? '';
};

const resolveClientTypeFromPort = (port: string): string => {
  if (port === resolveBmPort()) {
    return 'bm';
  }
  return '';
};

export const resolveCurrentClientType = (): string => {
  const env = (import.meta as { env?: Record<string, string | undefined> }).env;
  const envType = normalizeClientType(env?.VITE_CLIENT_TYPE);
  if (envType) {
    return envType;
  }
  if (typeof window === 'undefined') {
    return '';
  }
  const { hostname, port } = window.location;
  if (isProductionEnv()) {
    const hostType = resolveClientTypeFromHost(hostname);
    if (hostType) {
      return hostType;
    }
  }
  return resolveClientTypeFromPort(port);
};

export const buildClientRedirectHost = (hostname: string, targetClientType: string): string => {
  const normalizedTarget = normalizeClientType(targetClientType);
  const { subdomains, baseDomain } = splitHostLabels(hostname);
  const preservedSubdomains = subdomains.filter((part) => !KNOWN_CLIENT_TYPE_HOST_LABELS.has(part));
  return normalizedTarget
    ? [...preservedSubdomains, normalizedTarget, baseDomain].join('.')
    : preservedSubdomains.length > 0
      ? [...preservedSubdomains, baseDomain].join('.')
      : baseDomain;
};

export const buildClientRedirectUrl = (targetClientType: string): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }
  if (!isProductionEnv()) {
    return null;
  }
  const normalizedTarget = normalizeClientType(targetClientType);
  const currentType = resolveCurrentClientType();
  if (normalizedTarget === currentType) {
    return null;
  }

  const { protocol, hostname } = window.location;
  if (LOCALHOST_HOSTS.has(hostname)) {
    const targetPort = normalizedTarget === 'bm' ? resolveBmPort() : resolveOriginalPort();
    return `${protocol}//${hostname}:${targetPort}/login`;
  }

  const targetHost = buildClientRedirectHost(hostname, normalizedTarget);
  return `${protocol}//${targetHost}/login`;
};
